import { getDiffDateInSec } from "../utils/utils";
import { STORAGE_KEYS, Storage } from "../utils/store";
import {initHttpClient} from "../core/http-client";
import { getDate, HOURS_12_IN_SEC } from "./utils";

const httpClient = initHttpClient();

export class TraccarClient {
    static WS_NAME = 'traccar';

    #app;
    #connectionInfo;
    #devices = new Map();
    #layer = L.layerGroup([]);
    #isConnecting = false;

    constructor(app) {
        this.#app = app;
        this.#connectionInfo = {
            'url': process.env.GENDLOC_TRACCAR_URL,
            'token': null
        };
    }

    connect() {
        if (this.#isConnecting) { return; }

        this.#isConnecting = true;
        this.#connectionInfo.token = Storage.get(STORAGE_KEYS.USER)?.unit?.traccar_token;

        try {
            this.#webSocket();
        } finally {
            this.#isConnecting = false;
        }
    }

    deviceLastPosition(positionIds) {
        const { url, token } = this.#connectionInfo; 

        return httpClient.get(
            `${url}/api/positions`,
            {
                params: positionIds.map(id => ['id', id]),
                headers: { 'Authorization': `Bearer ${token}` }
            }
        );
    }

    devices(ids = undefined) {
        const { url, token } = this.#connectionInfo;
        const headers =  { 'Authorization': `Bearer ${token}` };

        const deviceFilter = (dev) => getDiffDateInSec(new Date(), new Date(dev.lastUpdate)) < HOURS_12_IN_SEC && dev.positionId > 0;
        const errorHandler = (error) => {
            console.error(error);
            M.toast({html: `Une erreur s'est produite avec Traccar.<br>Vérifiez si le token est valide`, classes: 'red'});

            return[];
        };

        return httpClient.get(`${url}/api/devices`, {params: ids?.map(id => ['id', id]), headers})
            .then(devices => (devices ?? []).filter(deviceFilter))
            .catch(errorHandler);
    }

    #webSocket() {
        const { url, token } = this.#connectionInfo;

        if (!url) { return Promise.reject('error.NO_TRACCAR_URL'); }
        if (!token) { return Promise.reject('error.NO_TRACCAR_TOKEN'); }

        const wsUrl = `${url}/api/socket?${new URLSearchParams({ token }).toString()}`;

        this.#app.registerWs(TraccarClient.WS_NAME, wsUrl);
    }

    addTo(map) {
        this.#layer.addTo(map);

        import('./layers').then(() => this.#fetchDevices())
            .then(() => {
                this.#app.onWsMessage(TraccarClient.WS_NAME, (data) => {
                    if ('devices' in data) {
                        this.#updateDevices(data.devices);
                    }   
                    
                    if ('positions' in data) {
                        this.#updatePositions(data.positions);
                    }
                });
            });
    }

    async #fetchDevices(devices = undefined) {
        return this.devices(devices)
            .then(async devices => {
                const devicePositionIds = devices.map(({ positionId }) => positionId);
                
                return [devices, await this.deviceLastPosition(devicePositionIds)];
            })
            .then(this.#initLayer)
            .then(layers => {
                layers.forEach(([id, layer]) => {
                    this.#devices.set(id, layer);
                    this.#layer.addLayer(layer);
                });
            });
    }

    async #initLayer([devices, positions]) {
        return devices.map(dev => {
            dev.lastPosition = positions.find(({ deviceId }) => deviceId === dev.id);

            return [dev.id, L.traccarPoint(dev)];            
        });
    }

    async #updatePositions(positions) {
        const unknownDeviceIds = [];

        positions.forEach(position => {
            if (!this.#devices.has(position.deviceId)) {
                unknownDeviceIds.push(position.deviceId);
            } else {
                this.#devices.get(position.deviceId).updatePosition(position);
            }
        });
        
        if (unknownDeviceIds.length < 1) { return; }

        this.#fetchDevices(unknownDeviceIds);
    }

    async #updateDevices(devices) {
        const unknownDeviceIds = [];

        devices.forEach(dev => {
            if (!this.#devices.has(dev.id)) {
                unknownDeviceIds.push(dev.id);
            } else {
                this.#devices.get(dev.id).updateDevice(dev);
            }
        });

        if (unknownDeviceIds.length < 1) { return; }

        this.#fetchDevices(unknownDeviceIds);
    }
}
